import { useEffect, useRef } from 'react'
import Big from 'big.js'

import type { MonetaryValueV2 } from 'models/purchaseFlowRevamp/catalogueV2DTO'
import { logger } from 'utils/logging'

import { usePfStore } from '../../store'

/**
 * How long a mismatch must persist before it is reported. A cart edit moves the displayed gross one
 * render before the provider flags the re-price as in flight, so for that moment the old intent
 * amount sits next to the new cart. A real mismatch outlives the re-price; that one does not.
 */
export const CHARGE_PARITY_SETTLE_MS = 1500

/**
 * Warns when the cart's displayed gross and the Stripe intent's amount disagree (ISSUE-4066): the
 * cart mirrors `ConcreteFee.asSmallestUnit`, so a difference means the client is about to be charged
 * a figure they were not shown. Silent until both are known, and once per displayed/charged pair.
 *
 * @param displayedGross the gross the cart shows, already rounded to the cent; `null` while no VAT rate is known
 * @param currency the currency the cart displays in
 * @param chargedAmount the intent's amount in major units; `null`/`undefined` when there is none to compare
 */
export function useChargeParityTripwire(displayedGross: Big | null, currency: string | null, chargedAmount?: MonetaryValueV2 | null) {
  // Primitives, not the Big: a new Big every render would re-run the effect and restart the settle timer.
  const displayed = displayedGross?.toFixed(2) ?? null
  const chargedValue = chargedAmount?.amount
  const chargedCurrency = chargedAmount?.currency ?? null
  const reportedRef = useRef<string | null>(null)

  useEffect(() => {
    if (displayed === null || chargedValue === undefined || chargedValue === null) return

    const charged = new Big(chargedValue).round(2, Big.roundHalfUp).toFixed(2)
    const isSameCurrency = !currency || !chargedCurrency || currency === chargedCurrency
    if (isSameCurrency && charged === displayed) return

    const pair = `${displayed} ${currency} / ${charged} ${chargedCurrency}`
    if (reportedRef.current === pair) return

    const timer = setTimeout(() => {
      reportedRef.current = pair
      // Read when it fires, not subscribed: it only identifies the session, it never decides anything.
      const sessionId = usePfStore.getState().draft.sessionId
      logger.warn('Cart gross differs from the payment intent amount', undefined, {
        context: {
          component: 'CartSummary',
          action: 'chargeParity',
          metadata: { displayed, charged, currency, chargedCurrency, sessionId },
        },
      })
    }, CHARGE_PARITY_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [displayed, currency, chargedValue, chargedCurrency])
}
